import React, { AriaRole, KeyboardEvent, useMemo } from 'react';

interface A11yCommonProps {
  role?: AriaRole;
  tabIndex?: number;
}

type InteractiveHTMLTag =
  | 'a'
  | 'area'
  | 'audio'
  | 'button'
  | 'input'
  | 'iframe'
  | 'select'
  | 'textarea'
  | 'video';

type NonInteractiveHTMLTag = Exclude<
  keyof JSX.IntrinsicElements,
  InteractiveHTMLTag
>;

interface A11yProps<T extends InteractiveHTMLTag | NonInteractiveHTMLTag>
  extends A11yCommonProps {
  as: T;
  customRef?: React.Ref<HTMLElement>;
}

export const handleKeyboardEnter =
  <T extends Element>(callback: (e: KeyboardEvent<T>) => void) =>
  (e: KeyboardEvent<T>) => {
    if (e.key === 'Enter') {
      callback(e);
    }
  };

export const A11yElement = <
  T extends InteractiveHTMLTag | NonInteractiveHTMLTag,
>({
  as: Element,
  role,
  tabIndex,
  children,
  onClick,
  onKeyDown,
  customRef,
  ...rest
}: A11yProps<T> & JSX.IntrinsicElements[T]) => {
  const allyRoleProps = useMemo(() => {
    const commonProps: JSX.IntrinsicElements[T] = {
      tabIndex,
      role,
      onKeyDown: undefined,
      ref: customRef,
    };

    switch (role as AriaRole) {
      case 'button':
      case 'link': {
        if (onKeyDown || onClick) {
          commonProps.onKeyDown = handleKeyboardEnter((e) => {
            onKeyDown?.(e);
            onClick?.(e as unknown as Parameters<typeof onClick>[0]);
          });
        }

        return {
          ...commonProps,
          tabIndex: tabIndex ?? 0,
        };
      }
      default:
        return commonProps;
    }
  }, [onClick, onKeyDown, role, tabIndex, customRef]);

  return (
    <Element
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      {...rest}
      {...allyRoleProps}
    >
      {children}
    </Element>
  );
};
// specific A11y components using the generic A11yComponent
export const DivA11y = React.forwardRef<
  HTMLDivElement,
  A11yCommonProps & React.HTMLAttributes<HTMLDivElement>
>(({ children, role = 'button', ...props }, ref) => (
  <A11yElement as="div" role={role} {...props} customRef={ref}>
    {children}
  </A11yElement>
));
